// MilestoneTape — 撕信 milestone list (W3.1 §5.2).
//
// Each milestone renders as a 「信紙」 card. achieved milestones flip
// immediately (status=achieved → 「撕開 → 展示文字 → 撕下」 3-stage
// animation, w3.1 ships simplified version). upcoming milestones show
// 「還有 N 天開啟」.

import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";

import { useAnniversaryMilestones } from "@/lib/hooks/anniversary";
import { daysUntil } from "@/lib/anniversary/countdown";
import { usePrefersReducedMotion } from "@/lib/anniversary/countUp";
import type { AnniversaryMilestoneRead } from "@/lib/api/anniversary";
import { LoadingState } from "@/components/primitives/LoadingState";
import { cn } from "@/lib/utils/cn";

export interface MilestoneTapeProps {
  anniversaryId: string;
}

function kindLabel(kind: string, value: number): string {
  if (kind === "days_100") return `100 天里程碑`;
  if (kind === "days_365") return `365 天里程碑`;
  if (kind === "years_n") return `${value} 週年`;
  return `${value} 里程碑`;
}

export function MilestoneTape({ anniversaryId }: MilestoneTapeProps) {
  const query = useAnniversaryMilestones(anniversaryId);
  const reduced = usePrefersReducedMotion();

  const items = (query.data?.pages ?? []).flatMap((p) => p.items).filter((m) => m.is_enabled);

  if (query.isLoading) {
    return <LoadingState count={2} shape="row" />;
  }
  if (items.length === 0) return null;

  return (
    <section
      data-component="MilestoneTape"
      className="flex flex-col gap-3"
      data-testid="milestone-tape"
    >
      <h3 className="font-display text-title-3 text-ink-1">里程碑</h3>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {items.map((m) => (
          <MilestoneCard key={m.id} milestone={m} reduced={reduced} />
        ))}
      </div>
      {query.hasNextPage ? (
        <button
          type="button"
          data-testid="milestone-load-more"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          className="press min-h-11 self-start rounded-full border border-hairline bg-paper-deep px-4 text-body-sm text-ink-1 transition-colors duration-quick hover:bg-paper-calm disabled:opacity-60"
        >
          {query.isFetchingNextPage ? "載入中…" : "載入更多里程碑"}
        </button>
      ) : null}
    </section>
  );
}

interface MilestoneCardProps {
  milestone: AnniversaryMilestoneRead;
  reduced: boolean;
}

function MilestoneCard({ milestone, reduced }: MilestoneCardProps) {
  // Animation state machine: idle → tearOpen → revealed → torn
  const [stage, setStage] = useState<"idle" | "open" | "torn">(
    reduced || milestone.status !== "achieved" ? "idle" : "idle",
  );
  const stageRef = useRef(stage);
  stageRef.current = stage;

  useEffect(() => {
    if (reduced) return;
    if (milestone.status !== "achieved") return;
    // Single tear cycle: open after 200ms, torn after 600ms.
    const t1 = window.setTimeout(() => setStage("open"), 200);
    const t2 = window.setTimeout(() => setStage("torn"), 800);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [milestone.status, reduced]);

  const label = kindLabel(milestone.kind, milestone.threshold_value);
  const achievedAt = milestone.achieved_at ? new Date(milestone.achieved_at) : null;
  const remainingDays = achievedAt ? daysUntil(achievedAt.toISOString().slice(0, 10)) : 0;

  return (
    <article
      data-component="MilestoneCard"
      data-status={milestone.status}
      data-stage={stage}
      className={cn(
        "shrink-0 w-44 rounded-md border p-3 flex flex-col gap-1.5",
        "bg-paper-tint1 border-hairline",
        milestone.status === "achieved" && "border-you/40",
        milestone.status === "upcoming" && "border-shared/30",
        milestone.status === "missed" && "border-partner/30 opacity-70",
      )}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-full",
            milestone.status === "achieved"
              ? "bg-you-tint text-you-strong"
              : "bg-shared-tint text-shared-strong",
          )}
        >
          <Sparkles size={14} strokeWidth={1.5} />
        </span>
        <p className="font-display text-body-sm font-semibold text-ink-1 truncate">{label}</p>
      </div>
      {milestone.status === "achieved" && achievedAt ? (
        <p
          className={cn(
            "text-meta tabular-nums",
            stage === "torn" ? "text-ink-3 line-through" : "text-you-strong",
          )}
        >
          {stage === "torn" ? "已撕下" : `${achievedAt.toISOString().slice(0, 10)} 開啟`}
        </p>
      ) : milestone.status === "upcoming" ? (
        <p className="text-meta text-shared-strong tabular-nums" data-testid="milestone-remaining">
          還有 {Math.abs(remainingDays)} 天開啟
        </p>
      ) : (
        <p className="text-meta text-partner-strong tabular-nums">已錯過</p>
      )}
    </article>
  );
}
